import { ChevronLeft, ChevronRight, Download, Eye, Mail, Phone, Users, UserCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../lib/api';
import AdminFilterBar from './AdminFilterBar';

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  user:  'bg-blue-100 text-blue-700',
};

// Profile images are stored as relative paths (`profiles/<file>` or
// `/uploads/...`). Resolve against the API host so the admin panel can
// load them from a different origin.
const resolveImageUrl = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (s.startsWith("/uploads/") || s.startsWith("/")) return `${base}${s}`;
  return `${base}/uploads/${s}`;
};

function UserAvatar({ user, size = 36 }) {
  const src = resolveImageUrl(user?.profile_image);
  const [failed, setFailed] = useState(false);
  const showImg = src && !failed;
  if (showImg) {
    return (
      <img
        src={src}
        alt={user?.name || ""}
        onError={() => setFailed(true)}
        className="rounded-full object-cover flex-shrink-0 border border-[#E8E6F8]"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#6C63FF] to-[#A78BFA] text-white flex items-center justify-center font-heading font-bold flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.34 }}
    >
      {(user?.name || "?")[0]?.toUpperCase()}
    </div>
  );
}

export default function UsersTable() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ search: '', role: '' });
  const [data, setData]       = useState({ users: [], total: 0, page: 1, pages: 1 });
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const load = useCallback(async (p = page) => {
    setLoading(true);
    setFetchError('');
    try {
      const params = { page: p, limit: 20 };
      if (filters.search) params.search = filters.search;
      if (filters.role)   params.role   = filters.role;
      const { data: res } = await API.get('/admin/users', { params });
      setData(res);
    } catch (err) {
      console.error(err);
      setFetchError(err.response?.data?.detail || err.message || 'Failed to load users');
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { load(page); }, [load, page]);

  const handleFilter = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };
  const handleReset = () => { setFilters({ search: '', role: '' }); setPage(1); };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // Page through every matching user — server caps `limit` at 100.
      const baseParams = {};
      if (filters.search) baseParams.search = filters.search;
      if (filters.role)   baseParams.role   = filters.role;

      const all = [];
      let p = 1;
      while (true) {
        const { data: res } = await API.get('/admin/users', {
          params: { ...baseParams, page: p, limit: 100 },
        });
        all.push(...(res.users || []));
        if (p >= (res.pages || 1) || (res.users || []).length === 0) break;
        p += 1;
      }

      const headers = [
        'User ID', 'Name', 'Email', 'Phone', 'Role', 'Status',
        'Assigned CA', 'Orders', 'City', 'State', 'Joined',
      ];

      // RFC 4180: quote if value contains comma / quote / newline.
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const rows = all.map((u) => [
        `#${u.id}`,
        u.name,
        u.email,
        u.phone,
        u.role,
        u.is_active ? 'Active' : 'Inactive',
        u.assigned_ca_name || 'Unassigned',
        u.order_count ?? 0,
        u.city,
        u.state,
        u.created_at ? new Date(u.created_at).toLocaleString('en-IN') : '',
      ].map(esc).join(','));

      const csv = [headers.map(esc).join(','), ...rows].join('\r\n');
      // UTF-8 BOM so Excel renders Indian names correctly.
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      const suffix = filters.role ? `-${filters.role}` : '';
      a.href = url;
      a.download = `users${suffix}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.response?.data?.detail || 'Failed to export users');
    }
    setExporting(false);
  };

  // Derived metrics from the visible page (cheap visual context).
  const activeOnPage = (data.users || []).filter((u) => u.is_active).length;
  const totalOrdersOnPage = (data.users || []).reduce((sum, u) => sum + (u.order_count || 0), 0);
  const unassignedOnPage = (data.users || []).filter((u) => !u.assigned_ca_name).length;

  return (
    <div className="space-y-5">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0C1A4D] via-[#1E3A8A] to-[#3B82F6] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#60A5FA]/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#A78BFA]/20 rounded-full blur-3xl" />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Customers
              </h1>
              <p className="text-sm text-white/75 font-body mt-0.5">
                {data.total} {data.total === 1 ? 'customer' : 'customers'} registered
              </p>
            </div>
          </div>

          {/* Mini KPIs */}
          <div className="flex items-center gap-3 sm:gap-5">
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">On Page</p>
              <p className="text-xl font-heading font-bold mt-0.5">{data.users.length}</p>
            </div>
            <div className="h-9 w-px bg-white/15" />
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Active</p>
              <p className="text-xl font-heading font-bold mt-0.5">{activeOnPage}</p>
            </div>
            <div className="h-9 w-px bg-white/15" />
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">Orders</p>
              <p className="text-xl font-heading font-bold mt-0.5">{totalOrdersOnPage}</p>
            </div>
            {unassignedOnPage > 0 && (
              <>
                <div className="h-9 w-px bg-white/15" />
                <div>
                  <p className="text-[10px] font-body uppercase tracking-[0.18em] text-orange-200">No CA</p>
                  <p className="text-xl font-heading font-bold mt-0.5 text-orange-200">{unassignedOnPage}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <AdminFilterBar filters={filters} onFilterChange={handleFilter} onReset={handleReset}>
        <select
          value={filters.role}
          onChange={(e) => handleFilter('role', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        <button
          onClick={handleExport}
          disabled={exporting || data.total === 0}
          title="Download all matching customers as CSV"
          className="flex items-center gap-1.5 text-xs font-body font-medium text-white bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition-colors"
        >
          {exporting ? (
            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </AdminFilterBar>

      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : fetchError ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-500 font-body">{fetchError}</p>
            <button onClick={() => load(page)} className="mt-2 text-xs text-[#6C63FF] underline font-body">Retry</button>
          </div>
        ) : data.users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Users className="h-8 w-8 text-[#9CA3AF] mb-2" />
            <p className="text-sm text-[#6B7280] font-body">No customers match these filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                  {['Customer', 'Contact', 'Role', 'Status', 'Assigned CA', 'Orders', 'Joined', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {data.users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => navigate(`/admin/users/${u.id}`)}
                    className="hover:bg-[#F5F4FB]/70 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <UserAvatar user={u} size={36} />
                        <div className="min-w-0">
                          <p className="font-medium text-[#1A1A2E] truncate">{u.name}</p>
                          <p className="text-[11px] text-[#9CA3AF] font-mono">#{u.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-xs">
                      <p className="flex items-center gap-1.5 text-[#4A4A60]">
                        <Mail className="h-3 w-3 text-[#9CA3AF]" />
                        <span className="truncate max-w-[200px]">{u.email}</span>
                      </p>
                      {u.phone && (
                        <p className="flex items-center gap-1.5 text-[#6B7280] mt-1">
                          <Phone className="h-3 w-3 text-[#9CA3AF]" />
                          {u.phone}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs">
                      {u.assigned_ca_name ? (
                        <div className="flex items-center gap-1.5">
                          <UserCheck className="h-3 w-3 text-[#6C63FF]" />
                          <span className="text-[#1A1A2E]">{u.assigned_ca_name}</span>
                        </div>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 font-medium">
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex items-center justify-center min-w-[28px] h-6 rounded-full bg-[#6C63FF]/10 text-[#6C63FF] font-heading font-bold text-xs px-2">
                        {u.order_count}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-[#9CA3AF] whitespace-nowrap">
                      {new Date(u.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 justify-end">
                        {u.phone && (
                          <a
                            href={`https://wa.me/${String(u.phone).replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`WhatsApp ${u.name || 'user'}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white transition-colors"
                          >
                            <WhatsAppIcon className="h-3.5 w-3.5" />
                          </a>
                        )}
                        <button
                          onClick={() => navigate(`/admin/users/${u.id}`)}
                          className="inline-flex items-center gap-1 text-xs text-[#6C63FF] font-medium px-2.5 py-1 rounded-lg hover:bg-[#6C63FF]/10 transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5" /> View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, data.total)} of {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">Page {page} / {data.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page === data.pages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/>
    </svg>
  );
}
